/**
 * Commitments -- Loan Participations FI-tenant commitment view.
 *
 * Sprint C14 Lane B WP B-7.
 *
 * The LSP API surface declares no caller-scoped commitments read. This page
 * lists GET /api/v1/syndication/offerings, which the server scopes to the
 * caller's OWN listings (syndication.routes.ts; the /marketplace route is the
 * cross-tenant browse), then reads each listing's commitment book via
 * `GET /api/v1/syndication/offerings/{id}/commitments`. That book holds every
 * commitment on the listing: the server masks other institutions' ids but
 * filters nothing. So a commitment the caller makes to another institution's
 * listing is never read here, and the rows below are the commitments recorded
 * on the caller's own listings (r3 fi-g9-1). Until a participant-scoped read is
 * declared in loan-syndication-protocol.openapi.yaml, the page withholds its
 * totals and says why rather than reporting 0 commitments and $0. Ownership
 * of each row is operator ruling 25.
 *
 * Implementation: list all offerings, then issue parallel
 * `commitments` queries for each offering and aggregate. Filtered to
 * offerings where the FI has at least one commitment (post-masking
 * length > 0).
 *
 * Drilldown: clicking a row routes back to the marketplace with the
 * offering pre-selected (future) or surfaces a detail panel inline.
 * For C14 the row reveal is an inline expansion showing offering
 * metadata + commit detail.
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Inbox } from 'lucide-react';
import {
  DataGrid,
  Skeleton,
  Heading,
  Card,
  Badge,
  Button,
  EmptyState,
  MetricCard,
  MetricCardGrid,
  METRIC_READ_CAPTIONS,
} from '@aaimplatform/ui';
import type { ColumnDef } from '@aaimplatform/ui';
import { useQuery, useQueries } from '@tanstack/react-query';
import type { components as LspComponents } from '@aaimplatform/institutional-types/loan-syndication-protocol';
import { loanSyndicationClient } from '../../api/client';
import {
  formatCurrencyFull,
  formatPercent,
  formatRatioPercent,
  formatDate,
  formatLeadDisplay,
  COMMITMENT_STATUS_LABELS,
} from './format';
import { SyndicationReadError, toSyndicationReadError } from './read-error';
import { ReadErrorBanner } from './ReadErrorBanner';

type ParticipationCommitment = LspComponents['schemas']['ParticipationCommitment'];
type ParticipationOffering = LspComponents['schemas']['ParticipationOffering'];

interface CommitmentRow extends ParticipationCommitment {
  offering: ParticipationOffering;
}

function getCommitmentStatusVariant(
  status: ParticipationCommitment['status'],
): 'success' | 'warning' | 'info' | 'neutral' | 'error' {
  switch (status) {
    // A standing commitment is a recorded state, not information to call out:
    // neutral, like WITHDRAWN and RELEASED. The blue 'info' chip read as a notice.
    case 'COMMITTED':
      return 'neutral';
    case 'ACTIVATED':
      return 'success';
    case 'PENDING_COAGENT_APPROVAL':
      return 'warning';
    case 'WITHDRAWN':
      return 'neutral';
    case 'RELEASED':
      return 'neutral';
    default:
      return 'neutral';
  }
}

/** The caption on the withheld commitment totals (r3 fi-g9-1). */
const COMMITMENT_TOTALS_CAPTION = 'Unavailable';

/** A per-offering commitments read that means "not visible to you", not "failed". */
export function isNotVisibleToCaller(error: SyndicationReadError): boolean {
  return error.status === 404 && error.code === 'OFFERING_NOT_FOUND';
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

function useAllOfferings() {
  return useQuery({
    queryKey: ['lsp-offerings', 'all-status'],
    queryFn: async () => {
      // Fetch a wide net (every status but a canceled offering) so Commitments
      // surfaces both pending (OPEN) commits and active participations.
      const { data, error, response } = await loanSyndicationClient.GET(
        '/api/v1/syndication/offerings',
        {
          params: {
            query: { page: 1, pageSize: 100 },
          },
        },
      );
      // Read before the error narrowing: on a route whose contract declares
      // no error body, openapi-fetch types that branch as never.
      const httpStatus = response.status;
      if (error) {
        throw toSyndicationReadError(error, httpStatus, 'Failed to load offerings');
      }
      return data;
    },
    staleTime: 60 * 1000,
    retry: false,
  });
}

function useOfferingCommitments(offerings: ParticipationOffering[]) {
  return useQueries({
    queries: offerings.map((offering) => ({
      queryKey: ['lsp-commitments', offering.offeringId],
      queryFn: async () => {
        const { data, error, response } = await loanSyndicationClient.GET(
          '/api/v1/syndication/offerings/{offeringId}/commitments',
          {
            params: { path: { offeringId: offering.offeringId } },
          },
        );
        // Read before the error narrowing: on a route whose contract declares
        // no error body, openapi-fetch types that branch as never.
        const httpStatus = response.status;
        if (error) {
          const readError = toSyndicationReadError(
            error,
            httpStatus,
            'Failed to load commitments',
          );
          // Only "this offering's book is not visible to you" is an empty
          // result: the list route answers 404 OFFERING_NOT_FOUND for an
          // offering that is neither the caller's nor browseable, and for a
          // private invite the caller is not on. Any other failure (a 401, a
          // 403, a 5xx) is a failed read and is surfaced, so the page never
          // reports "no commitments" and $0 over a request that did not
          // complete (fi-g9-15).
          if (isNotVisibleToCaller(readError)) {
            return { offering, commitments: [] as ParticipationCommitment[] };
          }
          throw readError;
        }
        return {
          offering,
          commitments: data?.commitments ?? [],
        };
      },
      staleTime: 60 * 1000,
      retry: false,
    })),
  });
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function Commitments() {
  const navigate = useNavigate();
  const [expandedRow, setExpandedRow] = useState<string | null>(null);

  const { data, isLoading: offeringsLoading, error: offeringsError } = useAllOfferings();
  const offerings = data?.offerings ?? [];
  const commitmentQueries = useOfferingCommitments(offerings);

  const isLoading =
    offeringsLoading || commitmentQueries.some((q) => q.isLoading);
  // One failed per-offering read leaves every total below incomplete, so it
  // fails the page the same way the offerings read does.
  const error = offeringsError ?? commitmentQueries.find((q) => q.error)?.error ?? null;

  const rows = useMemo<CommitmentRow[]>(() => {
    const out: CommitmentRow[] = [];
    commitmentQueries.forEach((q) => {
      if (!q.data) return;
      const { offering, commitments } = q.data;
      // After C-3 server-side masking, FI-tenant callers only receive
      // their own commitments for each offering. We trust the masked
      // shape -- no client-side filter required.
      commitments.forEach((c) => {
        out.push({ ...c, offering });
      });
    });
    return out;
  }, [commitmentQueries]);

  const columns = useMemo<ColumnDef<CommitmentRow, unknown>[]>(
    () => [
      {
        id: 'lead',
        header: 'Lead arranger',
        cell: ({ row }) => (
          <div>
            <div className="text-sm font-sans font-medium text-primary leading-normal">
              {formatLeadDisplay(row.original.offering)}
            </div>
            <div className="text-xs font-mono text-tertiary tabular-nums leading-normal">
              {row.original.offering.offeringId.slice(0, 8)}...
            </div>
          </div>
        ),
        size: 240,
      },
      {
        accessorKey: 'amount',
        header: 'Committed',
        cell: ({ getValue }) => (
          <span className="text-sm font-mono text-primary tabular-nums leading-normal">
            {formatCurrencyFull(getValue() as number)}
          </span>
        ),
        size: 160,
      },
      {
        accessorKey: 'participationShare',
        header: 'Share',
        cell: ({ getValue }) => (
          <span className="text-sm font-mono text-primary tabular-nums leading-normal">
            {formatRatioPercent(getValue() as number)}
          </span>
        ),
        size: 100,
      },
      {
        id: 'rate',
        header: 'Rate',
        cell: ({ row }) => (
          <span className="text-sm font-mono text-primary tabular-nums leading-normal">
            {formatPercent(row.original.offering.loanDetails.interestRate)}
          </span>
        ),
        size: 90,
      },
      {
        id: 'term',
        header: 'Term',
        cell: ({ row }) => (
          <span className="text-sm font-mono text-primary tabular-nums leading-normal">
            {row.original.offering.loanDetails.termMonths} mo
          </span>
        ),
        size: 80,
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => (
          <Badge variant={getCommitmentStatusVariant(row.original.status)} size="sm">
            {COMMITMENT_STATUS_LABELS[row.original.status] ?? row.original.status}
          </Badge>
        ),
        size: 200,
      },
      {
        accessorKey: 'committedAt',
        header: 'Committed at',
        cell: ({ getValue }) => (
          <span className="text-sm font-sans text-secondary leading-normal">
            {formatDate(getValue() as string)}
          </span>
        ),
        size: 140,
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => (
          <Button
            variant="secondary"
            size="sm"
            onClick={() =>
              setExpandedRow(
                expandedRow === row.original.commitmentId
                  ? null
                  : row.original.commitmentId,
              )
            }
          >
            {expandedRow === row.original.commitmentId ? 'Hide' : 'Detail'}
          </Button>
        ),
        size: 100,
      },
    ],
    [expandedRow],
  );

  // Render the expansion panel for the active row separately. The
  // DataGrid does not support row-expansion natively at this version;
  // surface the detail as a Card below the grid keyed on expandedRow.
  const expanded = useMemo(
    () => rows.find((r) => r.commitmentId === expandedRow) ?? null,
    [rows, expandedRow],
  );

  const showEmptyState = !isLoading && !error && rows.length === 0;

  return (
    <div>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          {/* Page title lives in the sticky topnav (AppHeader); the body opens
              with the descriptive subtitle, no redundant H1. */}
          <p className="text-sm font-sans text-tertiary">
            Active and pending commitments across the Loan Participations
            network. Other institutions' identities are masked.
          </p>
        </div>
        {/* One marketplace CTA on the page: the empty state carries it when
            there is nothing to list, this header slot otherwise. */}
        {!showEmptyState && (
          <Button variant="primary" onClick={() => navigate('/syndication/marketplace')}>
            Browse marketplace
          </Button>
        )}
      </div>

      {error && (
        <ReadErrorBanner
          error={error}
          subject="commitments"
          surface="Loan Participations -- Commitments"
        />
      )}

      <div
        role="note"
        className="bg-elevated border border-recessed rounded-sm p-4 mb-6 text-sm font-sans text-secondary"
      >
        Commitments your institution makes to other institutions&apos; listings cannot be read yet:
        the network has no read of an institution&apos;s own commitments. The totals are withheld,
        and the list shows commitments recorded on your institution&apos;s own listings.
      </div>

      <MetricCardGrid columns={3} className="mb-6">
        {isLoading ? (
          Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-24" />)
        ) : error ? (
          <>
            <MetricCard label="Total Commitments" value="--" caption={METRIC_READ_CAPTIONS.failed} />
            <MetricCard label="Committed" value="--" caption={METRIC_READ_CAPTIONS.failed} />
            <MetricCard label="Active Participations" value="--" caption={METRIC_READ_CAPTIONS.failed} />
          </>
        ) : (
          // Withheld, not zero: the only read available covers the caller's
          // own listings, so these totals cannot describe the caller's
          // commitments (r3 fi-g9-1).
          <>
            <MetricCard label="Total Commitments" value="--" caption={COMMITMENT_TOTALS_CAPTION} />
            <MetricCard label="Committed" value="--" caption={COMMITMENT_TOTALS_CAPTION} />
            <MetricCard label="Active Participations" value="--" caption={COMMITMENT_TOTALS_CAPTION} />
          </>
        )}
      </MetricCardGrid>

      {isLoading ? (
        <Card className="p-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-14 w-full mb-2" />
          ))}
        </Card>
      ) : error ? null : rows.length === 0 ? (
        // The kit EmptyState with the secondary marketplace action Compare
        // Deals uses; this was a hand-built card with a red primary button
        // (r3 fi-cons-6).
        <Card>
          <EmptyState
            icon={<Inbox className="h-8 w-8" />}
            title="No commitments on your listings"
            description="No commitment is recorded on your institution's own listings. Commitments your institution made to other listings cannot be shown yet."
            action={
              <Button variant="secondary" onClick={() => navigate('/syndication/marketplace')}>
                Browse marketplace
              </Button>
            }
          />
        </Card>
      ) : (
        <DataGrid<CommitmentRow>
          data={rows}
          columns={columns}
          enablePagination
          defaultPageSize={20}
          enableColumnResize
          hoverable
          getRowId={(row) => row.commitmentId}
          className="w-full rounded-sm shadow-sm"
        />
      )}

      {expanded && (
        <Card className="mt-6 p-6">
          <Heading level={2} size="card" className="mb-4">
            Commitment detail
          </Heading>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm font-sans">
            <div>
              <div className="text-xs text-tertiary mb-1">Commitment ID</div>
              <div className="font-mono text-primary tabular-nums break-all">
                {expanded.commitmentId}
              </div>
            </div>
            <div>
              <div className="text-xs text-tertiary mb-1">Offering ID</div>
              <div className="font-mono text-primary tabular-nums break-all">
                {expanded.offeringId}
              </div>
            </div>
            <div>
              <div className="text-xs text-tertiary mb-1">Loan amount</div>
              <div className="font-mono text-primary tabular-nums">
                {formatCurrencyFull(expanded.offering.loanDetails.loanAmount)}
              </div>
            </div>
            <div>
              <div className="text-xs text-tertiary mb-1">Total syndicated</div>
              <div className="font-mono text-primary tabular-nums">
                {formatCurrencyFull(
                  expanded.offering.participationStructure.totalSyndicationAmount,
                )}
              </div>
            </div>
            <div>
              <div className="text-xs text-tertiary mb-1">My commitment</div>
              <div className="font-mono text-primary tabular-nums">
                {formatCurrencyFull(expanded.amount)}
              </div>
            </div>
            <div>
              <div className="text-xs text-tertiary mb-1">Pro-rata share</div>
              <div className="font-mono text-primary tabular-nums">
                {formatRatioPercent(expanded.participationShare)}
              </div>
            </div>
            <div>
              <div className="text-xs text-tertiary mb-1">Co-agent approval</div>
              <div className="text-primary">
                {expanded.coAgentApprovalRequired ? 'Required' : 'Not required'}
              </div>
            </div>
            <div>
              <div className="text-xs text-tertiary mb-1">Collateral LTV</div>
              <div className="font-mono text-primary tabular-nums">
                {formatRatioPercent(expanded.offering.collateralSummary?.ltvRatio)}
              </div>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
